import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import {
  CheckCircle2,
  Clock,
  FileText,
  Mail,
  Send,
  XCircle,
} from 'lucide-react';
import { api } from '../../services/api.js';
import {
  downloadPdf,
  extractApiError,
  fetchPdfAsObjectUrl,
  isMobilePdfPreviewUnreliable,
  openPdfObjectUrl,
} from '../../utils/pdfDownload';
import {
  InvoiceIncompleteDialog,
  type InvoiceGap,
} from './InvoiceIncompleteDialog';
import { DocumentReassignControl } from './DocumentReassignControl';
import type { AttachedDocumentRef } from '../../utils/documentToast';
import { RemoveTourDialog } from '../delivery/RemoveTourDialog';
import { stopRemovableOnTour } from '../../utils/canRemoveFromTour';
import type { DeliveryTour } from '../../types/deliveryTour';

const STATUS_COLORS: Record<string, string> = {
  draft: 'bg-yellow-100 text-yellow-800',
  finalized: 'bg-blue-100 text-blue-800',
  sent: 'bg-green-100 text-green-800',
};

export interface DocumentLine {
  id: string;
  product_name: string;
  variant_name?: string | null;
  quantity: number;
  free_quantity?: number;
  unit_price: number;
  line_total: number;
  pfand_total: number;
}

export interface DocumentEmail {
  id: string;
  recipient_email: string;
  manufacturer_name?: string;
  distributor_name?: string;
  recipient_type?: string;
  subject?: string;
  status: string;
  error_message?: string;
  sent_at: string;
}

export interface DocumentDetail {
  id: string;
  number: string;
  status: string;
  pos_id?: string | null;
  pos_name?: string;
  pos_city?: string;
  manufacturer_name?: string;
  distributor_id?: string;
  distributor_name?: string;
  total_net: number;
  total_pfand: number;
  total_gross: number;
  items?: DocumentLine[];
  delivery_batch_id?: string | null;
}

interface DocumentDetailSheetProps {
  open: boolean;
  kind: 'lieferschein' | 'bestellung';
  doc: DocumentDetail | null;
  onClose: () => void;
  onRefresh: (docs?: AttachedDocumentRef[]) => void;
}

export const DocumentDetailSheet: React.FC<DocumentDetailSheetProps> = ({
  open,
  kind,
  doc,
  onClose,
  onRefresh,
}) => {
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [emails, setEmails] = useState<DocumentEmail[]>([]);
  const [sending, setSending] = useState(false);
  const [sendingStore, setSendingStore] = useState(false);
  const [invoiceGaps, setInvoiceGaps] = useState<InvoiceGap[]>([]);
  const [gapOpen, setGapOpen] = useState(false);
  const [removeOpen, setRemoveOpen] = useState(false);
  const [canRemoveStop, setCanRemoveStop] = useState(false);
  const useMobilePdfUi = isMobilePdfPreviewUnreliable();
  const base = kind === 'lieferschein' ? '/lieferscheine' : '/bestellungen';

  useEffect(() => {
    if (!open || !doc) return;
    let revoked: string | null = null;
    setPdfLoading(true);
    void fetchPdfAsObjectUrl(`${base}/${doc.id}/pdf`)
      .then((url) => {
        revoked = url;
        setPdfUrl(url);
      })
      .catch(() => setPdfUrl(null))
      .finally(() => setPdfLoading(false));
    void api
      .get(`${base}/${doc.id}/emails`)
      .then((res) => setEmails(res.data || []))
      .catch(() => setEmails([]));
    return () => {
      if (revoked) URL.revokeObjectURL(revoked);
    };
  }, [open, doc, base]);

  useEffect(() => {
    if (!open || kind !== 'lieferschein' || !doc?.delivery_batch_id) {
      setCanRemoveStop(false);
      return;
    }
    let cancelled = false;
    void api
      .get(`/delivery-batches/${doc.delivery_batch_id}`)
      .then((res) => {
        if (!cancelled) {
          setCanRemoveStop(
            stopRemovableOnTour(res.data as DeliveryTour, doc.pos_id)
          );
        }
      })
      .catch(() => {
        if (!cancelled) setCanRemoveStop(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, kind, doc]);

  if (!open || !doc) return null;

  const sendLabel =
    kind === 'lieferschein' ? 'Send to manufacturer' : 'Send to distributor';
  const sendPath =
    kind === 'lieferschein'
      ? `${base}/${doc.id}/send`
      : `${base}/${doc.id}/send-distributor`;

  const handleFinalize = async (skipInvoiceCheck = false) => {
    try {
      await api.patch(`${base}/${doc.id}/status`, {
        status: 'finalized',
        ...(skipInvoiceCheck ? { skip_invoice_check: true } : {}),
      });
      toast.success('Finalized');
      onRefresh();
    } catch (err) {
      const parsed = extractApiError(err);
      if (
        !skipInvoiceCheck &&
        kind === 'lieferschein' &&
        parsed.code === 'INVOICE_DATA_INCOMPLETE'
      ) {
        const details = Array.isArray(parsed.details)
          ? (parsed.details as InvoiceGap[])
          : [];
        setInvoiceGaps(details);
        setGapOpen(true);
        return;
      }
      toast.error(parsed.message);
      if (skipInvoiceCheck) throw err;
    }
  };

  const handleSend = async () => {
    setSending(true);
    try {
      await api.post(sendPath);
      toast.success('Sent');
      onRefresh();
    } catch (err) {
      toast.error(extractApiError(err).message);
    } finally {
      setSending(false);
    }
  };

  const handleSendStore = async () => {
    setSendingStore(true);
    try {
      await api.post(`${base}/${doc.id}/send-pos`);
      toast.success('Sent to store');
      onRefresh();
    } catch (err) {
      toast.error(extractApiError(err).message);
    } finally {
      setSendingStore(false);
    }
  };

  const handleRemoveFromTour = async (undoPick: boolean) => {
    try {
      await api.post(`${base}/${doc.id}/remove-from-tour`, { undo_pick: undoPick });
      toast.success(
        undoPick ? 'Removed from tour and pick undone' : 'Removed from tour, pick kept'
      );
      onClose();
      onRefresh();
    } catch (err) {
      toast.error(extractApiError(err).message);
      throw err;
    }
  };

  return (
    <>
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-end md:items-center justify-center p-0 md:p-4">
      <div className="bg-white w-full md:max-w-3xl rounded-t-[2rem] md:rounded-[2.5rem] shadow-2xl overflow-hidden max-h-[92vh] flex flex-col">
        <div className="p-5 md:p-6 overflow-y-auto flex-1">
          <div className="flex justify-between items-start mb-4">
            <div>
              <h2 className="text-xl font-black text-gray-900">{doc.number}</h2>
              <p className="text-sm text-gray-500">
                {doc.pos_name || 'No PoS'}
                {doc.pos_city ? ` · ${doc.pos_city}` : ''}
              </p>
              {doc.manufacturer_name && (
                <p className="text-sm text-gray-500">{doc.manufacturer_name}</p>
              )}
              {kind === 'bestellung' && doc.distributor_name && (
                <p className="text-sm text-gray-500">{doc.distributor_name}</p>
              )}
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 font-black text-sm min-h-[48px] px-3"
            >
              CLOSE
            </button>
          </div>

          <div className="flex flex-wrap items-center gap-2 mb-4">
            <span
              className={`px-3 py-1 rounded-full text-xs font-black uppercase ${STATUS_COLORS[doc.status] || ''}`}
            >
              {doc.status}
            </span>
          </div>

          <DocumentReassignControl
            kind={kind}
            docId={doc.id}
            docNumber={doc.number}
            status={doc.status}
            currentDistributorId={doc.distributor_id}
            onMoved={onRefresh}
          />

          <div className="grid grid-cols-3 gap-3 mb-6">
            <div className="bg-gray-50 rounded-xl p-3 text-center">
              <p className="text-xs font-bold text-gray-400 uppercase">Net</p>
              <p className="font-black text-gray-900">{Number(doc.total_net).toFixed(2)}</p>
            </div>
            <div className="bg-gray-50 rounded-xl p-3 text-center">
              <p className="text-xs font-bold text-gray-400 uppercase">Pfand</p>
              <p className="font-black text-gray-900">{Number(doc.total_pfand).toFixed(2)}</p>
            </div>
            <div className="bg-brand-50 rounded-xl p-3 text-center">
              <p className="text-xs font-bold text-brand-400 uppercase">Total</p>
              <p className="font-black text-brand-700">{Number(doc.total_gross).toFixed(2)}</p>
            </div>
          </div>

          {doc.items && doc.items.length > 0 && (
            <div className="mb-6 space-y-2">
              <h3 className="text-xs font-black text-gray-400 uppercase tracking-widest">
                Lines
              </h3>
              {doc.items.map((item) => (
                <div
                  key={item.id}
                  className="border border-gray-100 rounded-xl p-3 text-sm"
                >
                  <p className="font-bold text-gray-900">{item.product_name}</p>
                  <p className="text-xs text-gray-500">
                    {item.variant_name || '—'} · qty{' '}
                    {Number(item.quantity) + Number(item.free_quantity || 0)}
                  </p>
                </div>
              ))}
            </div>
          )}

          <details className="border-2 border-gray-100 rounded-xl overflow-hidden mb-4" open>
            <summary className="p-3 cursor-pointer flex items-center gap-2 bg-gray-50 font-bold text-sm text-gray-700">
              <FileText size={16} /> PDF Preview
            </summary>
            <div className="p-4">
              {pdfLoading ? (
                <div className="text-center py-12 text-sm text-gray-400 font-bold">
                  Generating PDF...
                </div>
              ) : pdfUrl ? (
                useMobilePdfUi ? (
                  <div className="flex flex-wrap gap-2 justify-center py-6">
                    <button
                      type="button"
                      onClick={() => openPdfObjectUrl(pdfUrl)}
                      className="flex items-center gap-1.5 px-4 py-3 bg-brand-600 text-white rounded-xl text-xs font-bold min-h-[48px]"
                    >
                      Open PDF
                    </button>
                    <button
                      type="button"
                      onClick={() => downloadPdf(`${base}/${doc.id}/pdf`, `${doc.number}.pdf`)}
                      className="flex items-center gap-1.5 px-4 py-3 bg-gray-900 text-white rounded-xl text-xs font-bold min-h-[48px]"
                    >
                      Download
                    </button>
                  </div>
              ) : (
                  <div className="space-y-2">
                    <iframe
                      src={pdfUrl}
                      className="w-full h-[420px] border border-gray-200 rounded-lg"
                      title="Document PDF preview"
                    />
                    <button
                      type="button"
                      onClick={() =>
                        downloadPdf(`${base}/${doc.id}/pdf`, `${doc.number}.pdf`)
                      }
                      className="text-xs font-bold text-gray-600 hover:text-gray-900"
                    >
                      Download PDF
                    </button>
                  </div>
                )
              ) : (
                <div className="text-center py-12 text-sm text-red-500 font-bold">
                  PDF not available
                </div>
              )}
            </div>
          </details>

          <details className="border-2 border-gray-100 rounded-xl overflow-hidden mb-24 md:mb-4">
            <summary className="p-3 cursor-pointer flex items-center gap-2 bg-gray-50 font-bold text-sm text-gray-700">
              <Mail size={16} /> Email Log ({emails.length})
            </summary>
            <div className="p-4 space-y-2">
              {emails.length === 0 ? (
                <p className="text-xs text-gray-400 text-center py-4">No sends yet</p>
              ) : (
                emails.map((e) => (
                  <div key={e.id} className="p-3 bg-gray-50 rounded-lg text-xs">
                    <div className="flex items-center gap-2">
                      {e.status === 'sent' ? (
                        <CheckCircle2 size={12} className="text-green-600" />
                      ) : (
                        <XCircle size={12} className="text-red-600" />
                      )}
                      <span className="font-bold">
                        {e.recipient_type === 'pos'
                          ? 'Store'
                          : e.manufacturer_name || e.distributor_name || 'Recipient'}
                      </span>
                    </div>
                    <p className="text-gray-400 truncate">{e.recipient_email}</p>
                    <p className="text-gray-400 flex items-center gap-1 mt-1">
                      <Clock size={11} />
                      {new Date(e.sent_at).toLocaleString('de-DE')}
                    </p>
                    {e.status === 'failed' && e.error_message && (
                      <p className="text-red-600 mt-1">{e.error_message}</p>
                    )}
                  </div>
                ))
              )}
            </div>
          </details>
        </div>

        <div className="border-t border-gray-100 p-4 flex flex-col gap-2 bg-white">
          <div className="flex gap-2">
            {doc.status === 'draft' && (
              <button
                onClick={() => void handleFinalize(false)}
                className="flex-1 min-h-[48px] px-4 bg-blue-600 text-white rounded-xl text-sm font-bold"
              >
                Finalize
              </button>
            )}
            <button
              onClick={() => void handleSend()}
              disabled={sending || (kind === 'lieferschein' && doc.status === 'draft')}
              className="flex-1 min-h-[48px] px-4 bg-brand-600 text-white rounded-xl text-sm font-bold disabled:opacity-40 flex items-center justify-center gap-2"
            >
              <Send size={16} />
              {sending ? 'Sending...' : sendLabel}
            </button>
          </div>
          {kind === 'lieferschein' &&
          doc.delivery_batch_id &&
          doc.status === 'draft' &&
          canRemoveStop ? (
            <button
              type="button"
              onClick={() => setRemoveOpen(true)}
              className="min-h-[48px] rounded-xl border-2 border-red-200 text-red-700 text-sm font-bold"
            >
              Remove from tour
            </button>
          ) : null}
          {kind === 'lieferschein' && (
            <button
              type="button"
              onClick={() => void handleSendStore()}
              disabled={sendingStore || doc.status === 'draft'}
              className="min-h-[48px] text-sm font-bold text-gray-600 hover:text-gray-900 disabled:opacity-40"
            >
              {sendingStore ? 'Sending...' : 'Send to store'}
            </button>
          )}
        </div>
      </div>
    </div>
    <InvoiceIncompleteDialog
      open={gapOpen}
      gaps={invoiceGaps}
      onClose={() => setGapOpen(false)}
      onForce={() => handleFinalize(true)}
    />
    <RemoveTourDialog
      open={removeOpen}
      onOpenChange={setRemoveOpen}
      storeName={doc.pos_name || doc.number}
      onUndoPick={() => handleRemoveFromTour(true)}
      onKeepPick={() => handleRemoveFromTour(false)}
    />
    </>
  );
};
